import { FC, ReactNode, useMemo } from 'react'
import {
  LAB_CEILING_LIGHTS,
  LAB_CUSTOM_STYLE,
  LAB_FINISH_SWATCHES,
  LAB_FLOOR_FINISHES,
  LAB_ROOM_TYPES,
  LAB_SPEEDS,
  LAB_SPEED_ESTIMATE_SECONDS,
  LAB_STAGE_MODES_WITH_SPEED,
  LAB_STAGING_STYLES,
  LAB_STYLE_CLAUSE_MAX,
  LAB_WALL_COLORS,
  LabCeilingLight,
  LabFloorFinish,
  LabRoomType,
  LabStageLook,
  LabStageMode,
  LabStageSettings,
  LabWallColor
} from './labSdk.types'
import { LabModeCard, LabSectionTitle, LabSegmented, LabSegmentedOption, LabSelectRow, LabTile, LabTileGrid } from './LabPanelPrimitives.component'
import type { GenQuality } from '@bkbnlab/api'
import { Stack, Typography } from '@mui/material'

import BorderAllOutlinedIcon from '@mui/icons-material/BorderAllOutlined'
import ChairOutlinedIcon from '@mui/icons-material/ChairOutlined'
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined'
import { GRAY_600, GRAY_700 } from 'constants/styling/theme'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import LightOutlinedIcon from '@mui/icons-material/LightOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIInputField } from 'components/common/MUIInputField'
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined'
import styleCoastal from 'assets/img/ai_visual_staging/livingroom/coastal.webp'
import styleContemporary from 'assets/img/ai_visual_staging/livingroom/contemporary.webp'
import styleDental from 'assets/img/ai_visual_staging/livingroom/dental.webp'
import styleGym from 'assets/img/ai_visual_staging/livingroom/gym.webp'
import styleIndustrial from 'assets/img/ai_visual_staging/livingroom/industrial.webp'
import styleLuxury from 'assets/img/ai_visual_staging/livingroom/luxury.webp'
import styleModern from 'assets/img/ai_visual_staging/livingroom/modern.webp'
import styleMountain from 'assets/img/ai_visual_staging/livingroom/mountain.webp'
import styleOffice from 'assets/img/ai_visual_staging/livingroom/office.webp'
import styleRestaurant from 'assets/img/ai_visual_staging/livingroom/restaurant.webp'
import styleRetail from 'assets/img/ai_visual_staging/livingroom/retail.webp'
import styleScandinavian from 'assets/img/ai_visual_staging/livingroom/scandinavian.webp'
import styleWarehouse from 'assets/img/ai_visual_staging/livingroom/warehouse.webp'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { hasLabCeilingLight, isLabRoomTypeLocked, resolveLabRoomType, roundLabSpeedEstimate, selectLabStageStyle } from './labSdk.mapping'
import { useTranslation } from 'react-i18next'

interface LabStageOptionsProps {
  settings: LabStageSettings
  onChange: (patch: Partial<LabStageSettings>) => void
  disabled?: boolean
}

const STYLE_THUMBS: Record<Exclude<LabStageLook, 'keep'>, string> = {
  contemporary: styleContemporary,
  modern: styleModern,
  scandinavian: styleScandinavian,
  luxury: styleLuxury,
  industrial: styleIndustrial,
  coastal: styleCoastal,
  mountain: styleMountain,
  office_space: styleOffice,
  warehouse: styleWarehouse,
  retail_store: styleRetail,
  gym: styleGym,
  restaurant: styleRestaurant,
  dental_practice: styleDental,
}

const MODE_ICONS: Record<LabStageMode, ReactNode> = {
  indoor_staging: <ChairOutlinedIcon />,
  declutter_local: <CleaningServicesOutlinedIcon />,
  mess_cleaner: <DeleteSweepOutlinedIcon />,
  outdoor_staging: <ParkOutlinedIcon />,
}

/** Modes that are new compared with today's Virtual Staging */
const NEW_MODES: LabStageMode[] = ['mess_cleaner', 'outdoor_staging']

/**
 * The Stage options of the Lab SDK engine — shown by the Stage panel and by the Stage section of "Display all features
 * at once". Four "Furnitures" cards; only "Stage a room" shows Room style (style tiles incl. "Keep as is", then the
 * room type) and the Surfaces selects (Walls / Floor, "Keep existing" by default; Ceiling light, "Match the style" by
 * default, with a home look only — hasLabCeilingLight). Speed sits last and only applies to
 * Stage a room and Stage a garden or terrace. Zone-scoped staging has no Lab equivalent and is absent.
 */
export const LabStageOptions: FC<LabStageOptionsProps> = ({ settings, onChange, disabled = false }) => {
  const { t, i18n } = useTranslation(['photo_studio_editing'])
  const { previewImage, imageUrl } = useClientPhotoStudio()

  const modeCards = useMemo(
    () => (['indoor_staging', 'declutter_local', 'mess_cleaner', 'outdoor_staging'] as LabStageMode[]).map((mode) => ({ mode, label: t(`lab.stage.mode.${mode}`), isNew: NEW_MODES.includes(mode) })),
    [t]
  )

  const styleTiles: LabTile[] = useMemo(
    () => [
      ...LAB_STAGING_STYLES.map((style): LabTile => style === 'keep'
        ? { id: style, label: t('lab.stage.keep_as_is'), image: previewImage || imageUrl, imageStyle: { filter: 'grayscale(.35)' } }
        : { id: style, label: t(`lab.stage.style.${style}`), image: STYLE_THUMBS[style] }),
      // The Lab Playground's "Describe the style": a mode next to the looks, not a look
      { id: LAB_CUSTOM_STYLE, label: t('lab.stage.style.custom'), placeholder: 'Aa' },
    ],
    [t, previewImage, imageUrl]
  )

  const roomTypeOptions = useMemo(
    () => LAB_ROOM_TYPES.map((type) => ({ value: type, label: type === 'auto' ? t('lab.stage.same_room_type') : t(`lab.stage.room_type.${type}`) })),
    [t]
  )
  const wallOptions = useMemo(
    () => LAB_WALL_COLORS.map((color) => ({ value: color, label: color === 'keep' ? t('lab.stage.keep_existing') : t(`lab.stage.wall.${color}`), swatch: LAB_FINISH_SWATCHES[color] })),
    [t]
  )
  const floorOptions = useMemo(
    () => LAB_FLOOR_FINISHES.map((finish) => ({ value: finish, label: finish === 'keep' ? t('lab.stage.keep_existing') : t(`lab.stage.floor.${finish}`), swatch: LAB_FINISH_SWATCHES[finish] })),
    [t]
  )
  const ceilingLightOptions = useMemo(
    () => LAB_CEILING_LIGHTS.map((choice) => ({ value: choice, label: choice === 'keep' ? t('lab.stage.keep_existing') : t('lab.stage.ceiling_light.style') })),
    [t]
  )
  // Quality: the run time sits under each tier, so the wait reads as the price of the detail before anyone clicks
  const speedOptions = useMemo(() => {
    const formatNumber = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 })
    return LAB_SPEEDS.map((speed): LabSegmentedOption<GenQuality> => {
      const estimate = roundLabSpeedEstimate(LAB_SPEED_ESTIMATE_SECONDS[speed])
      return {
        value: speed,
        label: t(`lab.stage.speed_option.${speed}`),
        caption: t(`lab.stage.speed_estimate.${estimate.unit}`, { value: formatNumber.format(estimate.value) }),
      }
    })
  }, [t, i18n.language])

  const isStageRoom = settings.mode === 'indoor_staging'
  const hasSpeed = LAB_STAGE_MODES_WITH_SPEED.includes(settings.mode)

  return (
    <>

      <Stack gap="0.6rem">

        <LabSectionTitle>{t('lab.stage.furnitures')}</LabSectionTitle>

        <Stack gap="0.6rem">
          {modeCards.map((card) => (
            <LabModeCard
              key={card.mode}
              icon={MODE_ICONS[card.mode]}
              label={card.label}
              selected={settings.mode === card.mode}
              onSelect={() => onChange({ mode: card.mode })}
              disabled={disabled}
              badge={card.isNew ? <MUIBadge label={t('lab.stage.new')} size="xs" color="blue" /> : undefined}
            />
          ))}
        </Stack>

      </Stack>

      {isStageRoom && (
        <Stack gap="0.8rem" sx={{ marginTop: '1.6rem' }}>

          <LabSectionTitle>{t('lab.stage.room_style')}</LabSectionTitle>

          <LabTileGrid
            tiles={styleTiles}
            selected={settings.customStyle ? LAB_CUSTOM_STYLE : settings.style}
            onSelect={(id) => onChange(selectLabStageStyle(settings, id))}
            disabled={disabled}
            columns={3}
            tileHeight="6.2rem"
            labelFontSize="1.2rem"
          />

          {/* Replaces the look's style text — says how the furniture looks; the room decides what gets furnished */}
          {settings.customStyle && (
            // The placeholder is the example to follow: stronger than MUI's faint 42% opacity, still clearly a placeholder
            <Stack gap="0.6rem" sx={{ marginBottom: '0.8rem', '& textarea::placeholder': { color: GRAY_600, opacity: 0.8 } }}>

              <MUIInputField
                isMultiline
                value={settings.styleClause}
                maxLength={LAB_STYLE_CLAUSE_MAX}
                placeholder={t('lab.stage.style_clause.placeholder')}
                onChange={(event) => onChange({ styleClause: event.target.value })}
                disabled={disabled}
              />

              <Typography sx={{ fontSize: '1.2rem', lineHeight: 1.4, color: GRAY_700 }}>
                {t('lab.stage.style_clause.hint')}
              </Typography>

            </Stack>
          )}

          {/* Room type only matters when the furnishing is re-staged — "Keep as is" changes finishes only */}
          {settings.style !== 'keep' && (
            // A business-space look stages as its own room type (Office): shown, disabled — the user's choice is kept for later
            <LabSelectRow<LabRoomType> icon={<HomeOutlinedIcon />} label={t('lab.stage.type')} value={resolveLabRoomType(settings)} options={roomTypeOptions} onChange={(roomType) => onChange({ roomType })} disabled={disabled || isLabRoomTypeLocked(settings)} />
          )}

        </Stack>
      )}

      {isStageRoom && (
        <Stack gap="0.6rem" sx={{ marginTop: '1.6rem' }}>

          <LabSectionTitle>{t('lab.stage.surfaces')}</LabSectionTitle>

          <LabSelectRow<LabWallColor> icon={<FormatPaintOutlinedIcon />} label={t('lab.stage.walls')} value={settings.wallColor} options={wallOptions} onChange={(wallColor) => onChange({ wallColor })} disabled={disabled} />

          <LabSelectRow<LabFloorFinish> icon={<BorderAllOutlinedIcon />} label={t('lab.stage.floor_label')} value={settings.floorFinish} options={floorOptions} onChange={(floorFinish) => onChange({ floorFinish })} disabled={disabled} />

          {/* Not with "Keep as is" (no look to restage it in) nor a business space (the light stays as photographed) */}
          {hasLabCeilingLight(settings) && (
            <LabSelectRow<LabCeilingLight> icon={<LightOutlinedIcon />} label={t('lab.stage.ceiling_light.label')} value={settings.ceilingLight} options={ceilingLightOptions} onChange={(ceilingLight) => onChange({ ceilingLight })} disabled={disabled} />
          )}

        </Stack>
      )}

      {hasSpeed && (
        <Stack gap="0.6rem" sx={{ marginTop: '1.6rem' }}>

          <LabSectionTitle>{t('lab.stage.speed')}</LabSectionTitle>

          <LabSegmented<GenQuality> options={speedOptions} value={settings.speed} onChange={(speed) => onChange({ speed })} disabled={disabled} />

          <Typography sx={{ fontSize: '1.2rem', fontWeight: 350, color: GRAY_700, lineHeight: 1.3, letterSpacing: '-0.48px' }}>
            {t(`lab.stage.speed_hint.${settings.speed}`)}
          </Typography>

        </Stack>
      )}

    </>
  )
}
